import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  PaginationItem,
  Divider,
  SelectChangeEvent,
} from "@mui/material";
import {
  Add,
  Delete,
  Download,
  FilterList,
  MoreVert,
  Search,
  FirstPage,
  LastPage,
  Remove,
  CloudUpload,
  Close,
  GridOn,
  PhotoCamera,
} from "@mui/icons-material";
import MapPinLocation from "../../../shared/components/MapPinLocation.tsx";
import { TrashOutline } from "react-ionicons";
import "../../../shared/components/Item.css";
import { styled } from "@mui/system";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { AxiosResponse } from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import FormHelperText from "@mui/material/FormHelperText";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  createBuildingRepair,
  buildingRepairSearchParams,
  fetchBuildingRepairPhotoBlob,
  deleteBuildingRepair,
  deleteBuildingRepairPhoto,
  updateBuildingRepair,
  uploadBuildingRepairPhoto,
  sesarchBuildingRepairs,
  fetchVendors,
  getBuildingRepairs,
  uploadBuildingRepairFile,
  deleteBuildingRepairFile,
  getBuildingRepairFile,
} from "../services/buildingRepairService.tsx";
import {
  getProfileData,
  loadProfileData,
  saveProfileData,
  ProfileData,
} from "../../user_profile/services/userService.tsx";
import { fetchEmployees, getEmployees } from "../../data_management/services/employeeService.tsx";
import { getUser } from "../../auth/services/authService.tsx";
import { getOfficeProjects } from "../services/projectDetailServices.tsx";
import { getRab, getRabDetail } from "../services/rabService.tsx";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import LoadingOverlay from "../../../shared/components/LoadingOverlay.tsx";

interface BuildingRepair {
  id: string;
  rabId: string;
  rabName?: string;
  rabNumber: string;
  ahsId: string;
  ahsName?: string;
  officeId: string;
  detailDescription: string;
  typeOfWork: string;
  repairCost: number;
  reportingUserId: string;
  volume: number;
  reportingStatus: string;
  duration: number;
  paymentDate: Date | null;
  approverName: string;
  approverPosition: string;
  isApprove: boolean | null;
  startDate: Date | null;
  endDate: Date | null;
  repairMethod: string;
  vendorId: string;
  vendorName?: string;
  paymentStatus: string;
  paymentEvidence?: string;
  damagePhoto?: string;
  createdAt?: Date;
  updatedAt?: Date;
  office?: {
    officeId: string;
    officeName: string;
    officeAddress: string;
    region: string;
    officeType: string;
    officeEmail: string;
    managerName: string;
    contactNumber: string;
    photoOffice: string;
    createdAt: string;
    updatedAt: string;
  };
  reporter?: {
    id: string;
    username: string;
    email: string;
    password: string;
    createdAt: string;
    updatedAt: string;
  };
}

interface AhsOption {
  id: string;
  namaAHS: string;
}

interface RabOption {
  rabId: string;
  rabName: string;
  rabNumber: string;
  officeId: string;
  officeName: string;
  grandTotal: string;
  ahsList: AhsOption[];
}

interface AdvancedSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSearchResults: (results: BuildingRepair[]) => void;
}

interface ProfileState extends ProfileData {
  // Extend if needed, or use ProfileData directly
}

const statuses = ["Not Approved", "Approved", "Rejected"];
const statusesValue = { "Not Approved": null, "Approved": true, "Rejected": false };

const BuildingRepairListPage = ({ user }: { user: any }) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState<BuildingRepair[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddBuildingRepairDialog, setOpenAddBuildingRepairDialog] =
    useState(false);
  const [openEditBuildingRepairDialog, setOpenEditBuildingRepairDialog] =
    useState(false);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [vendors, setVendors] = useState<any[]>([]);
  const [isVendorsLoading, setIsVendorsLoading] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  // const [vendors, setVendors] = useState<Array<{
  //     vendorId: string;
  //     vendorName: string;
  //   }>>([]);
  const [selectedVendor, setSelectedVendor] = useState<{
    vendorId: string;
    vendorName: string;
  } | null>(null);

  const [profile, setProfile] = useState<ProfileState | null>(null);
  const location = useLocation();
  const [employeeUsername, setEmployeeUsername] = useState<string>("");
  const [employeeOptions, setEmployeeOptions] = useState<any[]>([]);

  const [currentEmployee, setCurrentEmployee] = useState<{
    id: string;
    username: string;
  }>({ id: "", username: "" });

  const [officeOptions, setOfficeOptions] = useState<
    Array<{
      officeId: string;
      officeName: string;
    }>
  >([]);
  const [selectedOffice, setSelectedOffice] = useState<{
    officeId: string;
    officeName: string;
  } | null>(null);

  const [rabOptions, setRabOptions] = useState<RabOption[]>([]);
  const [selectedRab, setSelectedRab] = useState<RabOption | null>(null);
  const [ahsOptions, setAhsOptions] = useState<AhsOption[]>([]);
  const [selectedAhs, setSelectedAhs] = useState<AhsOption | null>(null);


  const [startDateS, setStartDateS] = useState<string | null>(null);
  const [endDateS, setEndDateS] = useState<string | null>(null);

  const [startDateE, setStartDateE] = useState<string | null>(null);
  const [endDateE, setEndDateE] = useState<string | null>(null);

  const [paymentDateStart, setStartDateP] = useState<string | null>(null);
  const [paymentDateEnd, setEndDateP] = useState<string | null>(null);

  // Edit Building Repair
  const [isEditing, setIsEditing] = useState(false);
  const [selectedBuildingRepairId, setSelectedBuildingRepairId] = useState<
    string | null
  >(null);
  const [buildingRepairDataEdit, setBuildingRepairDataEdit] =
    useState<BuildingRepair>({
      id: "",
      rabId: "",
      rabName: "",
      rabNumber: "",
      ahsId: "",
      ahsName: "",
      officeId: "",
      detailDescription: "",
      typeOfWork: "",
      repairCost: 0,
      reportingUserId: "",
      volume: 0,
      reportingStatus: "",
      duration: 0,
      paymentDate: new Date(),
      startDate: new Date(),
      endDate: new Date(),
      repairMethod: "",
      vendorId: "",
      vendorName: "",
      paymentStatus: "",
      isApprove: null,
      approverName: "",
      approverPosition: "",
    });

  const [editDamagePhotoFile, setEditDamagePhotoFile] = useState<File | null>(
    null
  );
  const [editPaymentEvidenceFile, setEditPaymentEvidenceFile] =
    useState<File | null>(null);
  const [editDamagePhotoPreview, setEditDamagePhotoPreview] =
    useState<string>("");
  const [editPaymentEvidencePreview, setEditPaymentEvidencePreview] =
    useState<string>("");

  const offset = (currentPage - 1) * rowsPerPage;

  const [filterParams, setFilterParams] = useState<buildingRepairSearchParams>(
    {}
  );

  // Sort
  const [sortConfig, setSortConfig] = useState<{
    orderBy: string;
    order: "asc" | "desc";
  }>({
    orderBy: "createdAt",
    order: "desc",
  });

  const fetchOfficeProjectList = async () => {
    try {
      const result = await getOfficeProjects();
      setOfficeOptions(
        result.data.map((project: any) => ({
          officeId: project.office.officeId,
          officeName: project.office.officeName,
        }))
      );
    } catch (error) {
      console.error("Failed to fetch offices:", error);
    }
  };

  const fetchEmployeeList = async () => {
    try {
      const result = await fetchEmployees();
      setEmployeeOptions(result); // Simpan data item dari backend
    } catch (error) {
      console.error("Failed to fetch items:", error);
    }
  };

  const fetchRabProjectList = async () => {
    try {
      const result = await getRab();
      if (!result.data || result.data.length === 0) {
        setRabOptions([]);
        return;
      }

      const rabDetails = await getRabDetail({ rabId: result.data[0].id });

      setRabOptions(
        result.data.map((project: any) => {
          const matchingDetails = rabDetails.data.filter(
            (detail: any) => detail.rabId === project.id
          );

          // Ubah untuk menyimpan objek AHS lengkap
          const ahsList = matchingDetails
            .map((detail: any) => ({
              id: detail.ahs?.id || "",
              namaAHS: detail.ahs?.namaAHS || "",
            }))
            .filter((ahs: any) => ahs.namaAHS); // Filter untuk menghilangkan nilai undefined/null

          return {
            rabId: project.id,
            rabName: project.rabName,
            rabNumber: project.rabNumber,
            officeId: project.officeProject.office.officeId,
            officeName: project.officeProject.office.officeName,
            grandTotal: project.grandTotal,
            ahsList: ahsList, // Simpan daftar objek AHS
          };
        })
      );
    } catch (error) {
      console.error("Failed to fetch RAB projects:", error);
    }
  };

  // Load vendors for dropdown
  useEffect(() => {
    const loadVendors = async () => {
      setIsVendorsLoading(true);
      try {
        const vendorsData = await fetchVendors();
        setVendors(vendorsData);
      } catch (error) {
        console.error("Failed to fetch vendors:", error);
      } finally {
        setIsVendorsLoading(false);
      }
    };
    loadVendors();
    fetchEmployeeList();
  }, []);

  // Ensure the selected vendor is populated once vendors are loaded
  useEffect(() => {
    if (vendors.length && buildingRepairDataEdit.vendorName) {
      const match = vendors.find(
        (v) => String(v.vendorId) === String(buildingRepairDataEdit.vendorName)
      );
      setSelectedVendor(match || null);
    }
  }, [vendors, buildingRepairDataEdit.vendorName]);

  useEffect(() => {
    const loadData = async () => {
      const localData = loadProfileData();
      if (localData) {
        setCurrentEmployee({
          id: localData.id,
          username: localData.name,
        });
        return;
      }
      let emailToFetch = user?.email || localData?.email;

      if (!emailToFetch) {
        try {
          const authUser = await getUser();
          emailToFetch = authUser?.user?.email;
        } catch (err) {
          console.error("Failed to fetch user info:", err);
        }
      }

      if (emailToFetch) {
        try {
          const freshData = await getProfileData(emailToFetch);
          if (freshData) {
            setCurrentEmployee({
              id: freshData.id,
              username: freshData.name,
            });
          }
        } catch (err) {
          console.error("Failed to load profile:", err);
        }
      }
    };
    loadData();
  }, [location.state, user?.email]);

  // Fungsi untuk mengambil data BuildingRepair
  const reloadBuildingRepair = async () => {
    setIsLoading(true);
    setError("");
    try {
      const result = await getBuildingRepairs({
        ...filterParams,
        limit: rowsPerPage,
        offset,
        orderBy: sortConfig.orderBy,
        order: sortConfig.order,
      });

      // Transform the API data. Keep vendorName as ID similar to ahsName
      const transformedData = result.data.map((item) => ({
        ...item,
        officeId: item.office.officeId, // Flatten office ID if needed
        officeName: item.office.officeName, // Add office name if needed
        vendorId: item.vendorName, // preserve vendor id from backend
        vendorName: item.vendorName, // keep ID like ahsName behaviour
      }));

      setRows(transformedData);
      setTotalItems(result.total);
      setTotalPages(result.pagination.totalPages);
    } catch (err: any) {
      setError(err.message || "Failed to load building repairs");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (column: string) => {
    setSortConfig((prev) => {
      if (prev.orderBy === column) {
        return {
          orderBy: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      } else {
        return {
          orderBy: column,
          order: "asc",
        };
      }
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    reloadBuildingRepair();
    fetchOfficeProjectList();
    fetchRabProjectList();
  }, [currentPage, rowsPerPage, filterParams, sortConfig]);

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const CustomTextField = styled(TextField)({
    "& .MuiInputBase-input": {
      backgroundColor: "#f5f5f5",
      borderRadius: 4,
      padding: "8px 12px",
    },
  });

  const handleDeleteBuildingRepair = async () => {
    const confirmDelete = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!confirmDelete.isConfirmed) return;

    try {
      const rowsToDelete = [...selectedRows];
      for (const id of rowsToDelete) {
        await deleteBuildingRepair(id);
      }

      Swal.fire({
        icon: "success",
        title: "Building repair deleted successfully",
        confirmButtonColor: "#0F62FE",
      });

      setSelectedRows([]);
      reloadBuildingRepair();
      setOpenDeleteDialog(false);
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Failed to delete building repair",
        text:
          error.response?.data?.message || error.message || "An error occurred",
      });
    }
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [rows]);

  const [filters, setFilters] = useState([{ field: "rabName", value: "" }]);

  const availableFilters = [
    { value: "rabName", label: "RAB Name" },
    { value: "rabNumber", label: "RAB Number" },
    { value: "officeId", label: "Office Location" },
    // { value: "detailDescription", label: "Details" },
    { value: "repairCost", label: "Cost" },
    { value: "reportingStatus", label: "Status" },
    { value: "reportingUserId", label: "Reporter" },
    { value: "repairMethod", label: "Repair Method" },
    { value: "vendorName", label: "Vendor Name" },
    { value: "startDate", label: "Start Date" },
    { value: "paymentDate", label: "Payment Date" },
  ];

  const handleFilterChange = (index: number, key: string, value: string) => {
    const newFilters = [...filters];
    newFilters[index][key] = value;
    setFilters(newFilters);
  };

  const handleAddFilter = () => {
    setFilters([...filters, { field: "", value: "" }]);
  };

  const handleRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
    const name = filters[index].field;
    if (name === "startDate") { setStartDateS(null); setEndDateS(null); }
    if (name === "paymentDate") { setStartDateP(null); setEndDateP(null); }
  };

  const handleApplyFilters = async () => {
    setIsLoading(true);
    try {
      const searchParams: buildingRepairSearchParams = {};

      if (startDateS && endDateS) {
        if (startDateS === endDateS) {
          searchParams.startDate = await startDateS;
        } else {
          searchParams.startDateS = await startDateS;
          searchParams.endDateS = await endDateS;
        }
      }

      if (startDateS && !endDateS) {
        searchParams.startDate = await startDateS;
      }

      if (paymentDateStart && paymentDateEnd) {
        if (paymentDateStart === paymentDateEnd) {
          searchParams.paymentDate = await paymentDateStart;
        } else {
          searchParams.paymentDateStart = await paymentDateStart;
          searchParams.paymentDateEnd = await paymentDateEnd;
        }
      }

      if (paymentDateStart && !paymentDateEnd) {
        searchParams.paymentDate = await paymentDateStart;
      }

      filters.forEach((filter) => {
        if (filter.value.trim() !== "") {
          // Transform filter values to IDs where needed
          switch (filter.field) {
            case "officeId":
              // Find office ID by name
              const office = officeOptions.find(
                (o) => o.officeName === filter.value
              );
              searchParams.officeId = office?.officeId || filter.value;
              break;

            case "rabName":
              // Find RAB ID by name
              const rab = rabOptions.find((r) => r.rabName === filter.value);
              searchParams.rabName = rab?.rabId || filter.value;
              break;

            case "ahsName":
              // Find AHS ID by name
              const ahs = ahsOptions.find((a) => a.namaAHS === filter.value);
              searchParams.ahsName = ahs?.id || filter.value;
              break;

            case "vendorName":
              // Find vendor ID by name
              const vendor = vendors.find((v) => v.vendorName === filter.value);
              searchParams.vendorName = vendor?.vendorId || filter.value;
              break;

            default:
              // For other fields, use as-is
              searchParams[filter.field] = filter.value;
          }
        }
      });

      setFilterParams(searchParams);
      setOpenFilterDialog(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to fetch data",
        text: error.response?.data?.message || "An error occurred",
      });
      handleCloseFilter();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseFilter = () => {
    setOpenFilterDialog(false);
  };

  const handleResetFilters = () => {
    setFilters([{ field: "rabName", value: "" }]);
    setFilterParams({});
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setError("");
    try {
      setFilterParams({
        keyword: searchQuery,
      });
    } catch (err) {
      setError(err.message || "Search failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      Swal.fire({
        title: "Preparing Export...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const exportData = await getExportData();
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Building Repairs");
      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Building_Repairs_${date}.xlsx`);
      Swal.close();
    } catch (error) {
      console.error("Export failed:", error);
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        text: error.message || "Failed to export data",
      });
    }
  };

  const getExportData = async () => {
    const allRepairs = await getBuildingRepairs({
      ...filterParams,
      limit: 1000000,
      offset: 0,
      orderBy: sortConfig.orderBy,
      order: sortConfig.order,
    });

    const formatNumber = (value) => {
      if (value === null || value === undefined) return "";
      const num = Number(value);
      if (isNaN(num)) return value;
      return num.toString().replace(/,/g, "."); // Replace comma with dot
    };

    return allRepairs.data.map((repair) => ({
      "RAB Name": rabOptions.find(
        (rab) => rab.rabId === repair.rabName // Note: rabName is actually the ID in API
      )?.rabName || "-",
      "RAB Number": repair.rabNumber || "-",
      "Office": repair.office.officeName || "-",
      Details: repair.detailDescription || "-",
      "Type of Work": repair.typeOfWork || "-",
      "Repair Cost": Number(repair.repairCost) || "-",
      "Reporter": repair.reporter.email || "-",
      Volume: repair.volume || "-",
      Status: repair.reportingStatus || "-",
      Duration: repair.duration || "-",
      "Start Date": repair.startDate
        ? new Date(repair.startDate).toLocaleDateString()
        : "-",
      "End Date": repair.endDate
        ? new Date(repair.endDate).toLocaleDateString()
        : "-",
      "Repair Method": repair.repairMethod || "-",
      "Vendor Name": vendors.find(
        (v) => String(v.vendorId) === String(repair.vendorName)
      )?.vendorName || repair.vendorName || "-",
      "Payment Status": repair.paymentStatus || "-",
      "Payment Date": repair.paymentDate
        ? new Date(repair.paymentDate).toLocaleDateString()
        : "-",
      "Created At": repair.createdAt
        ? new Date(repair.createdAt).toLocaleString()
        : "-",
      "Updated At": repair.updatedAt
        ? new Date(repair.updatedAt).toLocaleString()
        : "-",
    }));
  };

  // Add Building Repair
  const [buildingRepairDataAdd, setBuildingRepairDataAdd] = useState<
    Omit<BuildingRepair, "id" | "createdAt" | "updatedAt">
  >({
    rabId: "",
    rabName: "",
    rabNumber: "",
    ahsId: "",
    ahsName: "",
    officeId: "",
    detailDescription: "",
    typeOfWork: "",
    repairCost: 0,
    reportingUserId: "",
    volume: 0,
    reportingStatus: "",
    duration: 0,
    paymentDate: new Date(),
    approverName: "",
    approverPosition: "",
    isApprove: null,
    startDate: new Date(),
    endDate: new Date(),
    repairMethod: "",
    vendorId: "",
    vendorName: "",
    paymentStatus: "",
  });

  const [damagePhotoFile, setDamagePhotoFile] = useState<File | null>(null);
  const [paymentEvidenceFile, setPaymentEvidenceFile] = useState<File | null>(
    null
  );
  const [damagePhotoPreview, setDamagePhotoPreview] = useState<string>("");
  const [paymentEvidencePreview, setPaymentEvidencePreview] =
    useState<string>("");

  const resetAddForm = () => {
    setBuildingRepairDataAdd({
      rabId: "",
      rabName: "",
      rabNumber: "",
      ahsId: "",
      ahsName: "",
      officeId: "",
      detailDescription: "",
      typeOfWork: "",
      repairCost: 0,
      reportingUserId: "",
      volume: 0,
      reportingStatus: "",
      duration: 0,
      paymentDate: new Date(),
      approverName: "",
      approverPosition: "",
      isApprove: null,
      startDate: new Date(),
      endDate: new Date(),
      repairMethod: "",
      vendorId: "",
      vendorName: "",
      paymentStatus: "",
    });
    setSelectedRab(null); // Reset selectedRab
    setSelectedVendor(null);
    setSelectedOffice(null); // Reset selectedOffice jika diperlukan
    setAhsOptions([]); // Tambahkan ini
    setSelectedAhs(null);
    setDamagePhotoFile(null);
    setPaymentEvidenceFile(null);
    setDamagePhotoPreview("");
    setPaymentEvidencePreview("");
  };

  const handleOpenAddDialog = () => {
    resetAddForm();
    // Set nilai reportingUserId dengan employeeId yang sudah diambil
    setBuildingRepairDataAdd((prev) => ({
      ...prev,
      reportingUserId: currentEmployee.id,
    }));
    setOpenAddBuildingRepairDialog(true);
  };

  const handleInputChangeAdd = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    // If repair method changed to Internal, clear any selected vendor
    if (name === "repairMethod" && value === "Internal") {
      setSelectedVendor(null);
      setBuildingRepairDataAdd({
        ...buildingRepairDataAdd,
        repairMethod: value,
        vendorId: "",
        vendorName: "",
      });
      return;
    }
    setBuildingRepairDataAdd({ ...buildingRepairDataAdd, [name]: value });
  };

  const handleNumberInputChangeAdd = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    let newData = { ...buildingRepairDataAdd, [name]: Number(value) };

    // Jika yang diubah adalah duration dan ada start date, hitung ulang end date
    if (name === "duration" && buildingRepairDataAdd.startDate) {
      newData.endDate = calculateEndDate(
        buildingRepairDataAdd.startDate,
        Number(value)
      );
    }

    setBuildingRepairDataAdd(newData);
  };

  const handleDateChangeAdd = (name: string, value: Date | null) => {
    let newData = { ...buildingRepairDataAdd, [name]: value };

    // Jika yang diubah adalah start date, hitung ulang end date
    if (name === "startDate" && value && buildingRepairDataAdd.duration) {
      newData.endDate = calculateEndDate(value, buildingRepairDataAdd.duration);
    }

    setBuildingRepairDataAdd(newData);
  };

  const [errorsAdd, setErrorsAdd] = useState({
    rabName: false,
    rabNumber: false,
    ahsName: false,
    officeId: false,
    detailDescription: false,
    typeOfWork: false,
    repairCost: false,
    reportingUserId: false,
    volume: false,
    reportingStatus: false,
    duration: false,
    startDate: false,
    endDate: false,
    repairMethod: false,
    vendorName: false,
    paymentStatus: false,
    paymentDate: false,
  });

  const validateFormAdd = () => {
    const newErrors = {
      rabId: !buildingRepairDataAdd.rabId,
      rabName: !buildingRepairDataAdd.rabName,
      rabNumber: !buildingRepairDataAdd.rabNumber,
      ahsId: !buildingRepairDataAdd.ahsId,
      ahsName: !buildingRepairDataAdd.ahsName,
      officeId: !buildingRepairDataAdd.officeId,
      detailDescription: !buildingRepairDataAdd.detailDescription,
      typeOfWork: !buildingRepairDataAdd.typeOfWork,
      repairCost: !buildingRepairDataAdd.repairCost,
      reportingUserId: !buildingRepairDataAdd.reportingUserId,
      volume: !buildingRepairDataAdd.volume,
      reportingStatus: !buildingRepairDataAdd.reportingStatus,
      duration: !buildingRepairDataAdd.duration,
      startDate: !buildingRepairDataAdd.startDate,
      endDate: !buildingRepairDataAdd.endDate,
      repairMethod: !buildingRepairDataAdd.repairMethod,
      vendorId:
        buildingRepairDataAdd.repairMethod === "Vendor" &&
        !buildingRepairDataAdd.vendorId,
      vendorName:
        buildingRepairDataAdd.repairMethod === "Vendor" &&
        !buildingRepairDataAdd.vendorName,
      paymentStatus: !buildingRepairDataAdd.paymentStatus,
      paymentDate: false,
    };
    setErrorsAdd(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const calculateEndDate = (
    startDate: Date | null,
    duration: number
  ): Date | null => {
    if (!startDate || !duration) return null;

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + duration);
    return endDate;
  };

  const showPopupMandatoryError = () => {
    Swal.fire({
      icon: "error",
      title: "Incomplete Data",
      text: "Please fill all mandatory fields.",
    });
  };

  const handleFileUpload = (file: File, type: "damagePhoto" | "paymentEvidence") => {
    const validTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
    const maxSize = 5 * 1024 * 1024; // 2MB

    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: "error",
        title: "Invalid file type",
        text: "Only PDF, PNG, JPG, and JPEG files are allowed",
      });
      return;
    }

    if (file.size > maxSize) {
      Swal.fire({
        icon: "error",
        title: "File too large",
        text: "Maximum file size is 2MB",
      });
      return;
    }

    const newPreviewUrl = file.type === "application/pdf"
      ? "pdf_uploaded"
      : URL.createObjectURL(file);

    if (type === "damagePhoto") {
      setDamagePhotoFile(file);
      setDamagePhotoPreview(newPreviewUrl);
    } else {
      setPaymentEvidenceFile(file);
      setPaymentEvidencePreview(newPreviewUrl);
    }
  };

  const generateDisplayDamageFilename = (rabName: string): string => {
    if (!rabName) return "Repair_unknown_project.pdf";

    // Cari project yang sesuai
    const selectedProject = rabOptions.find(
      (opt) =>
        opt.rabName === rabName ||
        opt.rabId === rabName
    );

    const actualProjectName = selectedProject?.rabName || rabName;

    // Bersihkan nama project untuk nama file
    const cleanProjectName = actualProjectName
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "")
      .substring(0, 50);

    return `Repair_Damage_${cleanProjectName}.pdf`;
  };

  const generateDisplayPaymentFilename = (rabName: string): string => {
    if (!rabName) return "Repair_unknown_project.pdf";

    // Cari project yang sesuai
    const selectedProject = rabOptions.find(
      (opt) =>
        opt.rabName === rabName ||
        opt.rabId === rabName
    );

    const actualProjectName = selectedProject?.rabName || rabName;

    // Bersihkan nama project untuk nama file
    const cleanProjectName = actualProjectName
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "")
      .substring(0, 50);

    return `Repair_Payment_${cleanProjectName}.pdf`;
  };

  const handleDownloadDamage = async (filename: string) => {
    try {
      const { blob } = await getBuildingRepairFile(filename);

      // Generate datetime string format: YYYYMMDD_HHMMSS
      const now = new Date();
      const datetime = now.toISOString()
        .replace(/[:.-]/g, "")  // Remove colons, dots, and dashes
        .replace('T', '_')      // Replace T with underscore
        .slice(0, 15);          // Take YYYYMMDD_HHMMSS (15 characters)

      // Generate base filename tanpa extension
      const baseFilename = generateDisplayPaymentFilename(buildingRepairDataEdit.rabName);

      // Hapus extension .pdf jika ada
      const filenameWithoutExt = baseFilename.endsWith('.pdf')
        ? baseFilename.slice(0, -4)
        : baseFilename;

      // Tambahkan datetime dan pastikan extension .pdf
      const downloadFilename = `${filenameWithoutExt}_${datetime}.pdf`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      a.remove();

    } catch (error) {
      console.error("Download error:", error);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: error instanceof Error ? error.message : "Failed to download file",
      });
    }
  };

  const handleDownloadPayment = async (filename: string) => {
    try {
      const { blob } = await getBuildingRepairFile(filename);

      // Generate datetime string format: YYYYMMDD_HHMMSS
      const now = new Date();
      const datetime = now.toISOString()
        .replace(/[:.-]/g, "")  // Remove colons, dots, and dashes
        .replace('T', '_')      // Replace T with underscore
        .slice(0, 15);          // Take YYYYMMDD_HHMMSS (15 characters)

      // Generate base filename tanpa extension
      const baseFilename = generateDisplayPaymentFilename(buildingRepairDataEdit.rabName);

      // Hapus extension .pdf jika ada
      const filenameWithoutExt = baseFilename.endsWith('.pdf')
        ? baseFilename.slice(0, -4)
        : baseFilename;

      // Tambahkan datetime dan pastikan extension .pdf
      const downloadFilename = `${filenameWithoutExt}_${datetime}.pdf`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      a.remove();

    } catch (error) {
      console.error("Download error:", error);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: error instanceof Error ? error.message : "Failed to download file",
      });
    }
  };

  // Handle file deletion for edit mode
  // Handle file deletion for edit mode
  const handleDeleteFileEdit = async (type: "damagePhoto" | "paymentEvidence") => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        if (buildingRepairDataEdit.id) {
          await deleteBuildingRepairFile(buildingRepairDataEdit.id, type);
        }

        // Clear file from state
        if (type === "damagePhoto") {
          setEditDamagePhotoFile(null);
          setEditDamagePhotoPreview("");
          setBuildingRepairDataEdit(prev => ({ ...prev, damagePhoto: "" }));
        } else {
          setEditPaymentEvidenceFile(null);
          setEditPaymentEvidencePreview("");
          setBuildingRepairDataEdit(prev => ({ ...prev, paymentEvidence: "" }));
        }

        Swal.fire(
          "Deleted!",
          "Your file has been deleted.",
          "success"
        );
      } catch (error) {
        console.error("Error deleting file:", error);
        Swal.fire({
          icon: "error",
          title: "Delete failed",
          text: error.response?.data?.message || "Failed to delete file",
        });
      }
    }
  };

  const damagePhotoInputRef = useRef<HTMLInputElement>(null);
  const paymentEvidenceInputRef = useRef<HTMLInputElement>(null);

  const handleCloseAdd = () => {
    setOpenAddBuildingRepairDialog(false);
    resetAddForm();
  };

  const handleSaveAdd = async () => {
    try {
      if (!validateFormAdd()) {
        showPopupMandatoryError();
        return;
      }

      Swal.fire({
        title: "Processing...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await createBuildingRepair(buildingRepairDataAdd);

      // Upload files jika ada
      if (damagePhotoFile) {
        await uploadBuildingRepairFile(response.id, "damagePhoto", damagePhotoFile);
      }

      if (paymentEvidenceFile) {
        await uploadBuildingRepairFile(response.id, "paymentEvidence", paymentEvidenceFile);
      }

      setOpenAddBuildingRepairDialog(false);
      await reloadBuildingRepair();

      Swal.fire({
        icon: "success",
        title: "Building repair created successfully",
        confirmButtonColor: "#0F62FE",
      });
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Failed to create building repair",
        text: error.response?.data?.message || error.message || "An error occurred",
      });
    }
  };

  const handleOpenEditDialog = async (buildingRepair: BuildingRepair) => {
    try {
      setSelectedBuildingRepairId(buildingRepair.id);
      const userJobTitle = employeeOptions.find(emp => emp.email === localStorage.getItem("email"))?.jobTitle || "";
      const isManager = (userJobTitle.toLowerCase().includes("manager") || (localStorage.getItem("username") == 'superadmin'));

      setIsApprover(isManager);

      const position = employeeOptions.find(emp => emp.id === buildingRepair.approverName)?.jobTitle;

      // 1. Find the matching RAB from rabOptions
      const selectedRab = rabOptions.find(
        (rab) => rab.rabId === buildingRepair.rabName
      );

      // 2. Find the matching AHS if RAB is found
      let selectedAhs;
      if (selectedRab) {
        selectedAhs = selectedRab.ahsList.find(
          (ahs) => ahs.id === buildingRepair.ahsName
        );
      }

      // 3. Find the matching vendor
      const selectedVendor = vendors.find(
        (v) => String(v.vendorId) === String(buildingRepair.vendorId)
      );

      // 4. Prepare the edit data with vendor id value stored in vendorName
      const editData = {
        ...buildingRepair,
        vendorName: buildingRepair.vendorId,
        position: position,
      };

      // 5. Set all the states
      setBuildingRepairDataEdit(editData);
      setSelectedRab(selectedRab || null);
      setSelectedAhs(selectedAhs || null);
      setSelectedVendor(selectedVendor || null);

      // 6. Load file previews if they exist - DIUBAH DARI PHOTO KE FILE
      if (buildingRepair.damagePhoto) {
        try {
          const { blob } = await getBuildingRepairFile(buildingRepair.damagePhoto);
          const fileType = blob.type;

          if (fileType === 'application/pdf') {
            setEditDamagePhotoPreview("pdf_uploaded");
            // Create a File object from blob for editing
            const file = new File([blob], buildingRepair.damagePhoto, { type: 'application/pdf' });
            setEditDamagePhotoFile(file);
          } else {
            const objectUrl = URL.createObjectURL(blob);
            setEditDamagePhotoPreview(objectUrl);
            const file = new File([blob], buildingRepair.damagePhoto, { type: fileType });
            setEditDamagePhotoFile(file);
          }
        } catch (error) {
          console.warn("Failed to load damage file:", error);
          setEditDamagePhotoPreview("");
        }
      }

      if (buildingRepair.paymentEvidence) {
        try {
          const { blob } = await getBuildingRepairFile(buildingRepair.paymentEvidence);
          const fileType = blob.type;

          if (fileType === 'application/pdf') {
            setEditPaymentEvidencePreview("pdf_uploaded");
            // Create a File object from blob for editing
            const file = new File([blob], buildingRepair.paymentEvidence, { type: 'application/pdf' });
            setEditPaymentEvidenceFile(file);
          } else {
            const objectUrl = URL.createObjectURL(blob);
            setEditPaymentEvidencePreview(objectUrl);
            const file = new File([blob], buildingRepair.paymentEvidence, { type: fileType });
            setEditPaymentEvidenceFile(file);
          }
        } catch (error) {
          console.warn("Failed to load payment evidence file:", error);
          setEditPaymentEvidencePreview("");
        }
      }

      setOpenEditBuildingRepairDialog(true);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to fetch building repair data:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to load building repair",
        text: error.message || "Data not found",
      });
    }
  };

  const handleInputChangeEdit = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: any } }
  ) => {
    const { name, value } = event.target;
    if (name === "repairMethod" && value === "Internal") {
      setSelectedVendor(null);
      setBuildingRepairDataEdit((prevData) => ({
        ...prevData,
        repairMethod: value,
        vendorId: "",
        vendorName: "",
      }));
      return;
    }
    setBuildingRepairDataEdit((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleNumberInputChangeEdit = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const numValue = Number(value);

    const newData = {
      ...buildingRepairDataEdit,
      [name]: numValue,
    };

    // Jika mengubah duration, update endDate
    if (name === "duration" && buildingRepairDataEdit.startDate) {
      const endDate = new Date(buildingRepairDataEdit.startDate);
      endDate.setDate(endDate.getDate() + numValue);
      newData.endDate = endDate;
    }

    setBuildingRepairDataEdit(newData);
  };

  const calculateDuration = (
    startDate: Date | null,
    endDate: Date | null
  ): number => {
    if (!startDate || !endDate) return 0;
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Konversi ke hari
  };

  const handleDateChangeEdit = (name: string, value: Date | null) => {
    if (!value) return;

    const newData = { ...buildingRepairDataEdit, [name]: value };

    console.log(newData);

    // Jika mengubah startDate, hitung ulang endDate
    if (name === "startDate" && buildingRepairDataEdit.duration) {
      const endDate = new Date(value);
      endDate.setDate(endDate.getDate() + buildingRepairDataEdit.duration);
      newData.endDate = endDate;
    }
    // Jika mengubah endDate, hitung ulang duration
    else if (name === "endDate" && buildingRepairDataEdit.startDate) {
      newData.duration = calculateDuration(
        buildingRepairDataEdit.startDate,
        value
      );
    }

    setBuildingRepairDataEdit(newData);
  };

  const handleEditFileUpload = (file: File, type: "damagePhoto" | "paymentEvidence") => {
    const validTypes = ["image/png", "image/jpeg", "image/jpg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];
    const maxSize = 5 * 1024 * 1024; // 2MB

    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: "error",
        title: "Invalid file type",
        text: "Only PDF, PNG, JPG, and JPEG files are allowed",
      });
      return;
    }

    if (file.size > maxSize) {
      Swal.fire({
        icon: "error",
        title: "File too large",
        text: "Maximum file size is 2MB",
      });
      return;
    }

    const newPreviewUrl = file.type === "application/pdf"
      ? "pdf_uploaded"
      : URL.createObjectURL(file);

    if (type === "damagePhoto") {
      setEditDamagePhotoFile(file);
      setEditDamagePhotoPreview(newPreviewUrl);
    } else {
      setEditPaymentEvidenceFile(file);
      setEditPaymentEvidencePreview(newPreviewUrl);
    }
  };

  const editDamagePhotoInputRef = useRef<HTMLInputElement>(null);
  const editPaymentEvidenceInputRef = useRef<HTMLInputElement>(null);

  const handleCloseEdit = () => {
    setOpenEditBuildingRepairDialog(false);
    setIsEditing(false);
    setSelectedBuildingRepairId(null);
  };

  const [errorsEdit, setErrorsEdit] = useState({
    rabName: false,
    rabNumber: false,
    ahsName: false,
    officeId: false,
    detailDescription: false,
    typeOfWork: false,
    repairCost: false,
    reportingUserId: false,
    volume: false,
    reportingStatus: false,
    duration: false,
    startDate: false,
    endDate: false,
    repairMethod: false,
    vendorName: false,
    paymentStatus: false,
    paymentDate: false,
  });

  const validateFormEdit = () => {
    const newErrors = {
      rabName: !buildingRepairDataEdit.rabName,
      rabNumber: !buildingRepairDataEdit.rabNumber,
      ahsName: !buildingRepairDataEdit.ahsName,
      officeId: !buildingRepairDataEdit.officeId,
      detailDescription: !buildingRepairDataEdit.detailDescription,
      typeOfWork: !buildingRepairDataEdit.typeOfWork,
      repairCost: !buildingRepairDataEdit.repairCost,
      reportingUserId: !buildingRepairDataEdit.reportingUserId,
      volume: !buildingRepairDataEdit.volume,
      reportingStatus: !buildingRepairDataEdit.reportingStatus,
      duration: !buildingRepairDataEdit.duration,
      startDate: !buildingRepairDataEdit.startDate,
      endDate: !buildingRepairDataEdit.endDate,
      repairMethod: !buildingRepairDataEdit.repairMethod,
      vendorName:
        buildingRepairDataEdit.repairMethod === "Vendor" &&
        !buildingRepairDataEdit.vendorName,
      paymentStatus: !buildingRepairDataEdit.paymentStatus,
      paymentDate: false,
    };
    setErrorsEdit(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSaveEdit = async () => {
    if (!validateFormEdit()) {
      showPopupMandatoryError();
      return;
    }

    try {
      const updatedRepair = await updateBuildingRepair(buildingRepairDataEdit);

      // Upload new files jika ada
      if (editDamagePhotoFile) {
        await uploadBuildingRepairFile(updatedRepair.id, "damagePhoto", editDamagePhotoFile);
      }
      if (editPaymentEvidenceFile) {
        await uploadBuildingRepairFile(updatedRepair.id, "paymentEvidence", editPaymentEvidenceFile);
      }

      Swal.fire({
        icon: "success",
        title: "Building repair updated successfully",
        confirmButtonColor: "#0F62FE",
      });

      setOpenEditBuildingRepairDialog(false);
      reloadBuildingRepair();
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Failed to update building repair",
        text: error.response?.data?.message || error.message || "An error occurred",
      });
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "Closed":
        return "statusRed";
      case "In Progress":
        return "statusYellow";
      case "Open":
        return "statusGreen";
      default:
        return "";
    }
  };

  return isLoading ? (
    <LoadingOverlay />
  ) : (
    <Box sx={{ padding: 3 }}>
      {/* Header */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}
      >
        <Typography variant="h6" fontWeight="bold">
          Maintenance & Repair List
        </Typography>
      </Box>
      {/* Toolbar */}
      <Box className="toolbar">
        {/* Left Controls */}
        <Box className="leftControls">
          <Typography>Show</Typography>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            size="small"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>

          {/* Search Button & Input */}
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={() => setShowSearch((prev) => !prev)}
            className="searchButton"
          >
            Search
          </Button>
          {showSearch && (
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search"
              value={searchQuery}
              onChange={async (e) => {
                await setSearchQuery(e.target.value);
              }}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              autoFocus
              className="searchInput"
            />
          )}

          {/* Error Display */}
          {error && <Typography color="error">{error}</Typography>}
        </Box>

        {/* Right Controls */}
        <Box className="rightControls">
          <Button
            startIcon={<Delete />}
            variant="text"
            onClick={handleDeleteBuildingRepair}
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>

          {/* Filter Button */}
          <Button
            startIcon={<FilterList />}
            variant="text"
            onClick={() => setOpenFilterDialog(true)}
          >
            Filters
          </Button>

          <Button
            startIcon={<Download />}
            variant="contained"
            className="exportButton"
            onClick={handleExport}
          >
            Export
          </Button>

          <Button
            startIcon={<Add />}
            variant="contained"
            className="addCategoryButton"
            onClick={handleOpenAddDialog}
          >
            Add New Maintenance & Repair
          </Button>
        </Box>
      </Box>
      <Box className="deleteContainer">
        <Typography variant="body2" className="detailsSubtitle">
          Details List Of Maintenance & Repair
        </Typography>
      </Box>
      {/* Table */}
      <TableContainer component={Paper} className="tableContainer">
        <Table className="table">
          <TableHead className="tableHead">
            <TableRow>
              <TableCell padding="checkbox" className="tableCell">
                <Checkbox
                  indeterminate={
                    selectedRows.length > 0 && selectedRows.length < rows.length
                  }
                  checked={selectedRows.length === rows.length}
                  onChange={() =>
                    setSelectedRows(
                      selectedRows.length === rows.length
                        ? []
                        : rows.map((row) => row.id)
                    )
                  }
                />
              </TableCell>
              <TableCell className="tableCell" width="50px">
                <strong>No</strong>
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("rabName")}
                style={{ cursor: "pointer" }}
              >
                <strong>RAB Name</strong>
                {sortConfig.orderBy === "rabName" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("rabNumber")}
                style={{ cursor: "pointer" }}
              >
                <strong>RAB Number</strong>
                {sortConfig.orderBy === "rabNumber" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("office.officeName")}
                style={{ cursor: "pointer" }}
              >
                <strong>Office</strong>
                {sortConfig.orderBy === "office.officeName" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("ahsName")}
                style={{ cursor: "pointer" }}
              >
                <strong>Description</strong>
                {sortConfig.orderBy === "ahsName" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("repairCost")}
                style={{ cursor: "pointer" }}
              >
                <strong>Cost</strong>
                {sortConfig.orderBy === "repairCost" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("reportingStatus")}
                style={{ cursor: "pointer" }}
              >
                <strong>Status</strong>
                {sortConfig.orderBy === "reportingStatus" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("reportingUserId")}
                style={{ cursor: "pointer" }}
              >
                <strong>Reporter</strong>
                {sortConfig.orderBy === "reportingUserId" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("repairMethod")}
                style={{ cursor: "pointer" }}
              >
                <strong>Method</strong>
                {sortConfig.orderBy === "repairMethod" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("vendorName")}
                style={{ cursor: "pointer" }}
              >
                <strong>Vendor</strong>
                {sortConfig.orderBy === "vendorName" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("paymentStatus")}
                style={{ cursor: "pointer" }}
              >
                <strong>Payment</strong>
                {sortConfig.orderBy === "paymentStatus" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell className="tableCell"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={12} align="center" style={{ color: "red" }}>
                  {error}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  No maintenance records found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => {
                // Find RAB details for the current row
                const rabDetail = rabOptions.find(
                  (rab) => rab.rabId === row.rabName
                );
                // Find AHS details if available
                const ahsDetail = rabDetail?.ahsList.find(
                  (ahs) => ahs.id === row.ahsName
                );
                const user = rabOptions.find(
                  (rab) => rab.rabId === row.rabName
                );

                return (
                  <TableRow
                    key={row.id}
                    selected={selectedRows.includes(row.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onChange={(event) => {
                          event.stopPropagation();
                          handleSelectRow(row.id);
                        }}
                      />
                    </TableCell>
                    <TableCell>{offset + index + 1}</TableCell>
                    <TableCell>
                      <strong>
                        {rabDetail ? rabDetail.rabName : row.rabName}
                      </strong>
                    </TableCell>
                    <TableCell>{row.rabNumber}</TableCell>
                    <TableCell>
                      {row.office?.officeName || row.officeId}
                    </TableCell>
                    <TableCell>
                      {ahsDetail ? ahsDetail.namaAHS : row.ahsName}
                    </TableCell>
                    <TableCell>
                      {new Intl.NumberFormat("id-ID", {
                        style: "currency",
                        currency: "IDR",
                        minimumFractionDigits: 0,
                      }).format(parseFloat(row.repairCost))}
                    </TableCell>
                    <TableCell>{row.reportingStatus}</TableCell>
                    <TableCell>
                      {row.reporter?.username || row.reportingUserId}
                    </TableCell>
                    <TableCell>{row.repairMethod}</TableCell>
                    <TableCell>
                      {row.repairMethod === "Internal"
                        ? "-"
                        : vendors.find(
                          (v) => String(v.vendorId) === String(row.vendorName)
                        )?.vendorName ||
                        row.vendorName ||
                        "-"}
                    </TableCell>
                    <TableCell>{row.paymentStatus}</TableCell>
                    <TableCell className="tableCell">
                      <IconButton onClick={() => handleOpenEditDialog(row)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      <Box className="paginationContainer">
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`Showing ${offset + 1} to ${Math.min(
            offset + rowsPerPage,
            totalItems
          )} of ${totalItems} entries`}
        </Typography>

        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(e, page) => setCurrentPage(page)}
          className="pagination"
          showFirstButton
          showLastButton
          renderItem={(item) => (
            <PaginationItem
              {...item}
              components={{ first: FirstPage, last: LastPage }}
            />
          )}
        />
      </Box>
      {/* Filter Dialog */}
      <Dialog
        open={openFilterDialog}
        onClose={() => setOpenFilterDialog(false)}
      >
        <DialogTitle>Advanced Search</DialogTitle>
        <DialogContent>
          {filters.map((filter, index) => {
            const usedFields = filters
              .map((f, i) => i !== index && f.field)
              .filter(Boolean);
            const availableOptions = availableFilters.filter(
              (opt) => !usedFields.includes(opt.value)
            );

            const statusOptions = ["Open", "In Progress", "Closed"];
            const paymentOptions = ["Paid", "Unpaid"];
            const methodOptions = ["Internal", "Vendor"];

            // Get unique values for dropdown options
            const rabNameOptions = [
              ...new Set(rows.map((row) => row.rabName)),
            ].filter(Boolean);
            const ahsNameOptions = [
              ...new Set(rows.map((row) => row.ahsName)),
            ].filter(Boolean);
            const officeNameOptions = [
              ...new Set(
                rows.map((row) => row.office?.officeName || row.officeId)
              ),
            ].filter(Boolean);
            const vendorNameOptions = [
              ...new Set(
                rows.map((row) =>
                  row.repairMethod === "Vendor"
                    ? vendors.find(
                      (v) => String(v.vendorId) === String(row.vendorName)
                    )?.vendorName || row.vendorName
                    : null
                )
              ),
            ].filter(Boolean);

            return (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                gap={1}
                mb={2}
              >
                <TextField
                  select
                  value={filter.field}
                  onChange={(e) =>
                    handleFilterChange(index, "field", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, minWidth: 160 }}
                >
                  {availableOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                {filter.field === "startDate" ? (
                  <>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Start Date"
                        format="YYYY-MM-DD"
                        value={startDateS ? dayjs(startDateS) : null}
                        onChange={(newValue) =>
                          setStartDateS(newValue ? newValue.format("YYYY-MM-DD") : null)
                        }
                        slotProps={{
                          textField: {
                            variant: "outlined",
                            size: "small",
                            sx: { mt: 1, minWidth: 150 },
                          },
                        }}
                      />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="End Date"
                        format="YYYY-MM-DD"
                        value={endDateS ? dayjs(endDateS) : null}
                        onChange={(newValue) =>
                          setEndDateS(newValue ? newValue.format("YYYY-MM-DD") : null)
                        }
                        slotProps={{
                          textField: {
                            variant: "outlined",
                            size: "small",
                            sx: { mt: 1, minWidth: 150 },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </>
                ) : filter.field === "paymentDate" ? (
                  <>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Start Date"
                        format="YYYY-MM-DD"
                        value={paymentDateStart ? dayjs(paymentDateStart) : null}
                        onChange={(newValue) =>
                          setStartDateP(newValue ? newValue.format("YYYY-MM-DD") : null)
                        }
                        slotProps={{
                          textField: {
                            variant: "outlined",
                            size: "small",
                            sx: { mt: 1, minWidth: 150 },
                          },
                        }}
                      />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="End Date"
                        format="YYYY-MM-DD"
                        value={paymentDateEnd ? dayjs(paymentDateEnd) : null}
                        onChange={(newValue) =>
                          setEndDateP(newValue ? newValue.format("YYYY-MM-DD") : null)
                        }
                        slotProps={{
                          textField: {
                            variant: "outlined",
                            size: "small",
                            sx: { mt: 1, minWidth: 150 },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </>
                ) : filter.field === "reportingStatus" ? (
                  <TextField
                    select
                    label="Status"
                    variant="outlined"
                    size="small"
                    value={filter.value}
                    onChange={(e) =>
                      handleFilterChange(index, "value", e.target.value)
                    }
                    sx={{ mt: 1, minWidth: 160 }}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : filter.field === "paymentStatus" ? (
                  <TextField
                    select
                    label="Payment Status"
                    variant="outlined"
                    size="small"
                    value={filter.value}
                    onChange={(e) =>
                      handleFilterChange(index, "value", e.target.value)
                    }
                    sx={{ mt: 1, minWidth: 160 }}
                  >
                    {paymentOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : filter.field === "repairMethod" ? (
                  <TextField
                    select
                    label="Repair Method"
                    variant="outlined"
                    size="small"
                    value={filter.value}
                    onChange={(e) =>
                      handleFilterChange(index, "value", e.target.value)
                    }
                    sx={{ mt: 1, minWidth: 160 }}
                  >
                    {methodOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : filter.field === "rabName" ? (
                  <Autocomplete
                    options={rabOptions}
                    getOptionLabel={(option) => option.rabName}
                    value={
                      rabOptions.find((r) => r.rabId === filter.value) || null
                    }
                    onChange={(event, newValue) => {
                      handleFilterChange(
                        index,
                        "value",
                        newValue?.rabName || ""
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="RAB Name"
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1, minWidth: 160 }}
                      />
                    )}
                  />
                ) : filter.field === "ahsName" ? (
                  <Autocomplete
                    options={ahsOptions}
                    getOptionLabel={(option) => option.namaAHS}
                    value={
                      ahsOptions.find((a) => a.id === filter.value) || null
                    }
                    onChange={(event, newValue) => {
                      handleFilterChange(
                        index,
                        "value",
                        newValue?.namaAHS || ""
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="AHS Name"
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1, minWidth: 160 }}
                      />
                    )}
                  />
                ) : filter.field === "officeId" ? (
                  <Autocomplete
                    options={officeOptions}
                    getOptionLabel={(option) => option.officeName}
                    value={
                      officeOptions.find((o) => o.officeId === filter.value) ||
                      null
                    }
                    onChange={(event, newValue) => {
                      handleFilterChange(
                        index,
                        "value",
                        newValue?.officeName || ""
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Office"
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1, minWidth: 160 }}
                      />
                    )}
                  />
                ) : filter.field === "vendorName" ? (
                  <Autocomplete
                    options={vendors}
                    getOptionLabel={(option) => option.vendorName}
                    value={
                      vendors.find((v) => v.vendorId === filter.value) || null
                    }
                    onChange={(event, newValue) => {
                      handleFilterChange(
                        index,
                        "value",
                        newValue?.vendorName || ""
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vendor Name"
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1, minWidth: 160 }}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    label="Value"
                    variant="outlined"
                    size="small"
                    value={filter.value}
                    onChange={(e) =>
                      handleFilterChange(index, "value", e.target.value)
                    }
                    sx={{ mt: 1 }}
                  />
                )}

                {index > 0 && (
                  <IconButton onClick={() => handleRemoveFilter(index)}>
                    <Remove />
                  </IconButton>
                )}
              </Box>
            );
          })}

          {filters.length < availableFilters.length && (
            <Button startIcon={<Add />} onClick={handleAddFilter}>
              Add Filter
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetFilters}>Reset Filter</Button>
          <Button onClick={handleCloseFilter}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Apply"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Add Building Repair Dialog */}
      <Dialog
        open={openAddBuildingRepairDialog}
        onClose={handleCloseAdd}
        fullWidth
        maxWidth="lg"
        sx={{ backdropFilter: "blur(5px)" }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Add New Maintenance Proposal
          </Typography>
          <IconButton onClick={handleCloseAdd} color="error">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6} sm={6}>
                <Autocomplete
                  options={rabOptions}
                  getOptionLabel={(option: RabOption) => option.rabName}
                  value={
                    rabOptions.find(
                      (opt) => opt.rabId === buildingRepairDataAdd.rabId
                    ) || null
                  }
                  onChange={(event, newValue: RabOption | null) => {
                    setSelectedRab(newValue);
                    if (newValue) {
                      setAhsOptions(newValue.ahsList);
                      setSelectedAhs(null);
                      setBuildingRepairDataAdd((prev) => ({
                        ...prev,
                        rabId: newValue.rabId, // Simpan ID, bukan nama
                        rabName: newValue.rabName,
                        rabNumber: newValue.rabNumber,
                        officeId: newValue.officeId,
                        // repairCost: parseFloat(newValue.grandTotal),
                        ahsName: "",
                      }));
                      setSelectedOffice({
                        officeId: newValue.officeId,
                        officeName: newValue.officeName,
                      });
                    } else {
                      setAhsOptions([]);
                      setSelectedAhs(null);
                      setBuildingRepairDataAdd((prev) => ({
                        ...prev,
                        rabId: "", // Reset ID juga
                        rabName: "",
                        rabNumber: "",
                        officeId: "",
                        repairCost: 0,
                        ahsName: "",
                      }));
                      setSelectedOffice(null);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="RAB Name"
                      margin="normal"
                      variant="outlined"
                      error={!buildingRepairDataAdd.rabId} // Validasi berdasarkan ID sekarang
                      helperText={
                        !buildingRepairDataAdd.rabId ? "Mandatory Field" : ""
                      }
                    />
                  )}
                />
                <Autocomplete
                  options={ahsOptions}
                  getOptionLabel={(option: AhsOption) => option.namaAHS}
                  value={
                    ahsOptions.find(
                      (ahs) => ahs.id === buildingRepairDataAdd.ahsId
                    ) || null
                  }
                  onChange={(event, newValue: AhsOption | null) => {
                    setSelectedAhs(newValue);
                    setBuildingRepairDataAdd((prev) => ({
                      ...prev,
                      ahsId: newValue ? newValue.id : "",
                      ahsName: newValue ? newValue.namaAHS : "",
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="AHS Name (Description)"
                      margin="normal"
                      variant="outlined"
                      error={!buildingRepairDataAdd.ahsId}
                      helperText={
                        !buildingRepairDataAdd.ahsId ? "Mandatory Field" : ""
                      }
                    />
                  )}
                  noOptionsText="No AHS available - Please select RAB first"
                />
                <TextField
                  fullWidth
                  label="Detail Description"
                  multiline
                  rows={3}
                  margin="normal"
                  variant="outlined"
                  value={buildingRepairDataAdd.detailDescription}
                  sx={{ mb: 1 }}
                  name="detailDescription"
                  error={!buildingRepairDataAdd.detailDescription}
                  helperText={
                    !buildingRepairDataAdd.detailDescription
                      ? "Mandatory Field"
                      : ""
                  }
                  onChange={handleInputChangeAdd}
                />
                <TextField
                  fullWidth
                  label="Reporting Employee ID"
                  margin="normal"
                  variant="outlined"
                  value={currentEmployee.username || "Loading..."}
                  sx={{ mb: 1 }}
                  disabled
                />
                <TextField
                  select
                  fullWidth
                  label="Reporting Status"
                  margin="normal"
                  variant="outlined"
                  value={buildingRepairDataAdd.reportingStatus}
                  sx={{ mb: 1 }}
                  name="reportingStatus"
                  error={!buildingRepairDataAdd.reportingStatus}
                  helperText={
                    !buildingRepairDataAdd.reportingStatus
                      ? "Mandatory Field"
                      : ""
                  }
                  onChange={handleInputChangeAdd}
                >
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                </TextField>
                {/* Damage Photo Upload */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #aaa",
                    padding: 4,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative",
                    mb: 2,
                  }}
                  onClick={() => damagePhotoInputRef.current?.click()}
                >
                  {damagePhotoPreview ? (
                    damagePhotoPreview === "pdf_uploaded" ? (
                      <>
                        <GridOn fontSize="large" color="primary" />
                        <Typography variant="body2" mt={1}>
                          {damagePhotoFile?.name || "Uploaded PDF"}
                        </Typography>
                      </>
                    ) : (
                      <img
                        src={damagePhotoPreview}
                        alt="Damage File Preview"
                        style={{
                          width: "100%",
                          height: "auto",
                          borderRadius: "8px",
                          objectFit: "contain",
                          maxHeight: 180,
                        }}
                      />
                    )
                  ) : (
                    <>
                      <GridOn fontSize="large" color="action" />
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Drag & drop or click to upload damage file
                      </Typography>
                    </>
                  )}
                  <input
                    hidden
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    type="file"
                    ref={damagePhotoInputRef}
                    onChange={(e) =>
                      e.target.files?.[0] &&
                      handleFileUpload(e.target.files[0], "damagePhoto")
                    }
                  />
                  <Typography variant="caption" mt={1}>
                    Max size: 5MB (PDF, CSV/XLS/XLSX, WORD)
                  </Typography>
                </Box>
                <TextField
                  select
                  fullWidth
                  label="Repair Method"
                  margin="normal"
                  variant="outlined"
                  value={buildingRepairDataAdd.repairMethod}
                  sx={{ mb: 1 }}
                  name="repairMethod"
                  error={!buildingRepairDataAdd.repairMethod}
                  helperText={
                    !buildingRepairDataAdd.repairMethod ? "Mandatory Field" : ""
                  }
                  onChange={handleInputChangeAdd}
                >
                  <MenuItem value="Internal">Internal</MenuItem>
                  <MenuItem value="Vendor">Vendor</MenuItem>
                </TextField>
                {buildingRepairDataAdd.repairMethod === "Vendor" && (
                  <Autocomplete
                    options={vendors}
                    getOptionLabel={(option) => option.vendorName}
                    isOptionEqualToValue={(option, value) =>
                      String(option.vendorId) === String(value.vendorId)
                    }
                    sx={{ mb: 1 }}
                    value={selectedVendor}
                    onChange={(event, newValue) => {
                      setSelectedVendor(newValue);
                      setBuildingRepairDataAdd({
                        ...buildingRepairDataAdd,
                        vendorId: newValue?.vendorId || "",
                        vendorName: newValue?.vendorId || "",
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vendor Name"
                        variant="outlined"
                        error={
                          buildingRepairDataAdd.repairMethod === "Vendor" &&
                          !buildingRepairDataAdd.vendorId
                        }
                        helperText={
                          buildingRepairDataAdd.repairMethod === "Vendor" &&
                            !buildingRepairDataAdd.vendorId
                            ? "Mandatory Field"
                            : ""
                        }
                      />
                    )}
                    noOptionsText="No vendors available"
                  />
                )}
              </Grid>
              <Grid item xs={6} sm={6}>
                <TextField
                  fullWidth
                  label="RAB Number"
                  margin="normal"
                  variant="outlined"
                  value={buildingRepairDataAdd.rabNumber}
                  sx={{ mb: 1 }}
                  name="rabNumber"
                  error={!buildingRepairDataAdd.rabNumber}
                  helperText={
                    !buildingRepairDataAdd.rabNumber ? "Mandatory Field" : ""
                  }
                  onChange={handleInputChangeAdd}
                  disabled
                />
                <Autocomplete
                  options={officeOptions}
                  getOptionLabel={(option) => option.officeName}
                  value={selectedOffice}
                  onChange={(event, newValue) => {
                    setSelectedOffice(newValue);
                    setBuildingRepairDataAdd((prev) => ({
                      ...prev,
                      officeId: newValue ? newValue.officeId : "", // Tidak perlu convert ke Number jika UUID
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Office"
                      margin="normal"
                      variant="outlined"
                      error={!buildingRepairDataAdd.officeId}
                      helperText={
                        !buildingRepairDataAdd.officeId ? "Mandatory Field" : ""
                      }
                    />
                  )}
                  disabled
                />
                <TextField
                  fullWidth
                  label="Type of Work"
                  margin="normal"
                  variant="outlined"
                  value={buildingRepairDataAdd.typeOfWork}
                  sx={{ mb: 1 }}
                  name="typeOfWork"
                  error={!buildingRepairDataAdd.typeOfWork}
                  helperText={
                    !buildingRepairDataAdd.typeOfWork ? "Mandatory Field" : ""
                  }
                  onChange={handleInputChangeAdd}
                />
                <TextField
                  fullWidth
                  label="Repair Cost"
                  margin="normal"
                  variant="outlined"
                  type="number"
                  value={buildingRepairDataAdd.repairCost}
                  sx={{ mb: 1 }}
                  name="repairCost"
                  error={!buildingRepairDataAdd.repairCost}
                  helperText={
                    !buildingRepairDataAdd.repairCost ? "Mandatory Field" : ""
                  }
                  onChange={handleNumberInputChangeAdd}
                />
                <TextField
                  fullWidth
                  label="Volume"
                  margin="normal"
                  variant="outlined"
                  type="number"
                  value={buildingRepairDataAdd.volume}
                  sx={{ mb: 1 }}
                  name="volume"
                  error={!buildingRepairDataAdd.volume}
                  helperText={
                    !buildingRepairDataAdd.volume ? "Mandatory Field" : ""
                  }
                  onChange={handleNumberInputChangeAdd}
                />
                <TextField
                  fullWidth
                  label="Duration (days)"
                  margin="normal"
                  variant="outlined"
                  type="number"
                  value={buildingRepairDataAdd.duration || ""}
                  sx={{ mb: 1 }}
                  name="duration"
                  error={!buildingRepairDataAdd.duration}
                  helperText={
                    !buildingRepairDataAdd.duration ? "Mandatory Field" : ""
                  }
                  onChange={handleNumberInputChangeAdd}
                  inputProps={{ min: 1 }} // Pastikan duration tidak negatif
                />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="Start Date"
                      format="YYYY-MM-DD"
                      value={
                        buildingRepairDataAdd.startDate
                          ? dayjs(buildingRepairDataAdd.startDate)
                          : null
                      }
                      onChange={(newValue) =>
                        handleDateChangeAdd(
                          "startDate",
                          newValue?.toDate() || null
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { mb: 1 },
                          error: !buildingRepairDataAdd.startDate,
                          helperText: !buildingRepairDataAdd.startDate
                            ? "Mandatory Field"
                            : "",
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="End Date"
                      format="YYYY-MM-DD"
                      value={
                        buildingRepairDataAdd.endDate
                          ? dayjs(buildingRepairDataAdd.endDate)
                          : null
                      }
                      onChange={(newValue) => {
                        // Biarkan manual input untuk end date jika diperlukan
                        handleDateChangeAdd(
                          "endDate",
                          newValue?.toDate() || null
                        );
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { mb: 1 },
                        },
                      }}
                      disabled
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Payment Status"
                    margin="normal"
                    variant="outlined"
                    value={buildingRepairDataAdd.paymentStatus}
                    sx={{ mb: 1 }}
                    name="paymentStatus"
                    error={!buildingRepairDataAdd.paymentStatus}
                    helperText={
                      !buildingRepairDataAdd.paymentStatus
                        ? "Mandatory Field"
                        : ""
                    }
                    onChange={handleInputChangeAdd}
                  >
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Unpaid">Unpaid</MenuItem>
                  </TextField>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="Payment Date"
                      format="YYYY-MM-DD"
                      value={
                        buildingRepairDataAdd.paymentDate
                          ? dayjs(buildingRepairDataAdd.paymentDate)
                          : null
                      }
                      onChange={(newValue) =>
                        handleDateChangeAdd(
                          "paymentDate",
                          newValue?.toDate() || null
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { mb: 1 },
                          margin: "normal",
                          // error: !buildingRepairDataAdd.paymentDate,
                          // helperText: !buildingRepairDataAdd.paymentDate
                          //   ? "Mandatory Field"
                          //   : "",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                {/* Payment Evidence Upload */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #aaa",
                    padding: 4,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative",
                    mb: 2,
                  }}
                  onClick={() => paymentEvidenceInputRef.current?.click()}
                >
                  {paymentEvidencePreview ? (
                    paymentEvidencePreview === "pdf_uploaded" ? (
                      <>
                        <GridOn fontSize="large" color="primary" />
                        <Typography variant="body2" mt={1}>
                          {paymentEvidenceFile?.name || "Uploaded PDF"}
                        </Typography>
                      </>
                    ) : (
                      <img
                        src={damagePhotoPreview}
                        alt="Payment Evidence File Preview"
                        style={{
                          width: "100%",
                          height: "auto",
                          borderRadius: "8px",
                          objectFit: "contain",
                          maxHeight: 180,
                        }}
                      />
                    )
                  ) : (
                    <>
                      <GridOn fontSize="large" color="action" />
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Drag & drop or click to upload file
                      </Typography>
                    </>
                  )}
                  <input
                    hidden
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    type="file"
                    ref={paymentEvidenceInputRef}
                    onChange={(e) =>
                      e.target.files?.[0] &&
                      handleFileUpload(e.target.files[0], "paymentEvidence")
                    }
                  />
                  <Typography variant="caption" mt={1}>
                    Max size: 5MB (PDF, CSV/XLS/XLSX, WORD)
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        {/* Buttons: Save & Close */}
        <DialogActions
          sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
        >
          <Button variant="outlined" color="error" onClick={handleCloseAdd}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleSaveAdd}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Building Repair Dialog */}
      <Dialog
        open={openEditBuildingRepairDialog}
        onClose={handleCloseEdit}
        fullWidth
        maxWidth="lg"
        sx={{ backdropFilter: "blur(5px)" }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {isEditing
              ? "Edit Maintenance Proposal"
              : "Maintenance Proposal Details"}
          </Typography>
          <IconButton onClick={handleCloseEdit} color="error">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6} sm={6}>
                <Autocomplete
                  options={rabOptions}
                  getOptionLabel={(option: RabOption) => option.rabName}
                  value={selectedRab} // Gunakan selectedRab sebagai value
                  onChange={(event, newValue: RabOption | null) => {
                    setSelectedRab(newValue);
                    if (newValue) {
                      setAhsOptions(newValue.ahsList);
                      setSelectedAhs(null);
                      setBuildingRepairDataEdit((prev) => ({
                        ...prev,
                        rabId: newValue.rabId,
                        rabName: newValue.rabName,
                        rabNumber: newValue.rabNumber,
                        officeId: newValue.officeName, // Simpan officeName
                        // repairCost: parseFloat(newValue.grandTotal),
                        ahsName: "",
                      }));
                    }
                  }}
                  disabled={!isEditing}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="RAB Name"
                      margin="normal"
                      variant="outlined"
                      error={isEditing && errorsEdit.rabName}
                      helperText={
                        isEditing && errorsEdit.rabName ? "Mandatory Field" : ""
                      }
                    />
                  )}
                />
                <Autocomplete
                  options={ahsOptions}
                  getOptionLabel={(option: AhsOption) => option.namaAHS}
                  value={selectedAhs} // Gunakan selectedAhs sebagai value
                  onChange={(event, newValue: AhsOption | null) => {
                    setSelectedAhs(newValue);
                    setBuildingRepairDataEdit((prev) => ({
                      ...prev,
                      ahsName: newValue ? newValue.namaAHS : "",
                    }));
                  }}
                  disabled={!isEditing || !buildingRepairDataEdit.rabId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="AHS Name (Description)"
                      margin="normal"
                      variant="outlined"
                      error={isEditing && errorsEdit.ahsName}
                      helperText={
                        isEditing && errorsEdit.ahsName ? "Mandatory Field" : ""
                      }
                    />
                  )}
                  noOptionsText="No AHS available - Please select RAB first"
                />
                <TextField
                  fullWidth
                  label="Detail Description"
                  multiline
                  rows={3}
                  margin="normal"
                  variant="outlined"
                  value={buildingRepairDataEdit.detailDescription}
                  sx={{ mb: 1 }}
                  name="detailDescription"
                  disabled={!isEditing}
                  error={isEditing && errorsEdit.detailDescription}
                  helperText={
                    isEditing && errorsEdit.detailDescription
                      ? "Mandatory Field"
                      : ""
                  }
                  onChange={handleInputChangeEdit}
                />
                <TextField
                  fullWidth
                  label="Reporting Employee"
                  margin="normal"
                  variant="outlined"
                  value={currentEmployee.username || "Loading..."}
                  sx={{ mb: 1 }}
                  disabled
                />
                <TextField
                  select
                  fullWidth
                  label="Reporting Status"
                  margin="normal"
                  variant="outlined"
                  value={buildingRepairDataEdit.reportingStatus}
                  sx={{ mb: 1 }}
                  name="reportingStatus"
                  disabled={!isEditing}
                  error={isEditing && errorsEdit.reportingStatus}
                  helperText={
                    isEditing && errorsEdit.reportingStatus
                      ? "Mandatory Field"
                      : ""
                  }
                  onChange={handleInputChangeEdit}
                >
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                </TextField>
                {/* Damage Photo Upload */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: editDamagePhotoPreview ? "2px solid #aaa" : "2px dashed #aaa",
                    padding: 4,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                    textAlign: "center",
                    cursor: isEditing ? "pointer" : "default",
                    position: "relative",
                    opacity: isEditing ? 1 : 0.7,
                  }}
                  onClick={() => isEditing && editDamagePhotoInputRef.current?.click()}
                >
                  {editDamagePhotoPreview ? (
                    editDamagePhotoPreview === "pdf_uploaded" ? (
                      <>
                        <GridOn fontSize="large" color={isEditing ? "primary" : "disabled"} />
                        <Typography variant="body2" mt={1}>
                          {generateDisplayDamageFilename(buildingRepairDataEdit.rabName)}
                        </Typography>
                        {buildingRepairDataEdit.damagePhoto && (
                          <Typography variant="caption" color="textSecondary">
                            Original ID: {buildingRepairDataEdit.damagePhoto}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <img
                          src={editDamagePhotoPreview}
                          alt="Damage File Preview"
                          style={{
                            width: "100%",
                            height: "auto",
                            borderRadius: "8px",
                            objectFit: "contain",
                            maxHeight: 180,
                          }}
                        />
                        {buildingRepairDataEdit.damagePhoto && (
                          <Typography variant="caption" color="textSecondary" mt={1}>
                            Original: {buildingRepairDataEdit.damagePhoto}
                          </Typography>
                        )}
                      </>
                    )
                  ) : (
                    <>
                      <GridOn fontSize="large" color={isEditing ? "action" : "disabled"} />
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        {isEditing
                          ? "Drag & drop or click to upload damage file evidence"
                          : "No damage file uploaded"}
                      </Typography>
                    </>
                  )}

                  {/* Download and Delete Buttons */}
                  {editDamagePhotoFile && isEditing && (
                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (buildingRepairDataEdit.damagePhoto) {
                            handleDownloadDamage(buildingRepairDataEdit.damagePhoto);
                          }
                        }}
                      >
                        DOWNLOAD
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFileEdit("damagePhoto");
                        }}
                      >
                        DELETE
                      </Button>
                    </Box>
                  )}

                  <input
                    hidden
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    type="file"
                    ref={editDamagePhotoInputRef}
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleEditFileUpload(e.target.files[0], "damagePhoto");
                      }
                    }}
                    disabled={!isEditing}
                  />
                  <Typography variant="caption" mt={1}>
                    Max size: 5MB (PDF, CSV/XLS/XLSX, WORD)
                  </Typography>
                </Box>
                <TextField
                  select
                  fullWidth
                  label="Repair Method*"
                  margin="normal"
                  variant="outlined"
                  value={buildingRepairDataEdit.repairMethod}
                  sx={{ mb: 1 }}
                  name="repairMethod"
                  disabled={!isEditing}
                  error={isEditing && errorsEdit.repairMethod}
                  helperText={
                    isEditing && errorsEdit.repairMethod
                      ? "Mandatory Field"
                      : ""
                  }
                  onChange={handleInputChangeEdit}
                >
                  <MenuItem value="Internal">Internal</MenuItem>
                  <MenuItem value="Vendor">Vendor</MenuItem>
                </TextField>
                {buildingRepairDataEdit.repairMethod === "Vendor" && (
                  <Autocomplete
                    options={vendors}
                    getOptionLabel={(option) => option.vendorName}
                    isOptionEqualToValue={(option, value) =>
                      String(option.vendorId) === String(value.vendorId)
                    }
                    sx={{ mb: 1 }}
                    value={selectedVendor}
                    onChange={(event, newValue) => {
                      setSelectedVendor(newValue);
                      setBuildingRepairDataEdit({
                        ...buildingRepairDataEdit,
                        vendorId: newValue?.vendorId || "",
                        vendorName: newValue?.vendorId || "",
                      });
                    }}
                    disabled={!isEditing}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vendor Name"
                        variant="outlined"
                        error={
                          isEditing &&
                          buildingRepairDataEdit.repairMethod === "Vendor" &&
                          errorsEdit.vendorName
                        }
                        helperText={
                          isEditing &&
                            buildingRepairDataEdit.repairMethod === "Vendor" &&
                            errorsEdit.vendorName
                            ? "Mandatory Field"
                            : ""
                        }
                      />
                    )}
                    noOptionsText="No vendors available"
                  />
                )}
              </Grid>
              <Grid item xs={6} sm={6}>
                <TextField
                  fullWidth
                  label="RAB Number"
                  margin="normal"
                  variant="outlined"
                  value={buildingRepairDataEdit.rabNumber}
                  sx={{ mb: 1 }}
                  name="rabNumber"
                  disabled={!isEditing}
                  error={isEditing && errorsEdit.rabNumber}
                  helperText={
                    isEditing && errorsEdit.rabNumber ? "Mandatory Field" : ""
                  }
                  onChange={handleInputChangeEdit}
                />
                <TextField
                  fullWidth
                  label="Office"
                  margin="normal"
                  variant="outlined"
                  value={
                    buildingRepairDataEdit.office?.officeName ||
                    buildingRepairDataEdit.officeId
                  }
                  sx={{ mb: 1 }}
                  disabled
                />
                <TextField
                  fullWidth
                  label="Type of Work"
                  margin="normal"
                  variant="outlined"
                  value={buildingRepairDataEdit.typeOfWork}
                  sx={{ mb: 1 }}
                  name="typeOfWork"
                  disabled={!isEditing}
                  error={isEditing && errorsEdit.typeOfWork}
                  helperText={
                    isEditing && errorsEdit.typeOfWork ? "Mandatory Field" : ""
                  }
                  onChange={handleInputChangeEdit}
                />
                <TextField
                  fullWidth
                  label="Repair Cost"
                  margin="normal"
                  variant="outlined"
                  type="number"
                  value={buildingRepairDataEdit.repairCost}
                  sx={{ mb: 1 }}
                  name="repairCost"
                  disabled={!isEditing}
                  error={isEditing && errorsEdit.repairCost}
                  helperText={
                    isEditing && errorsEdit.repairCost ? "Mandatory Field" : ""
                  }
                  onChange={handleNumberInputChangeEdit}
                />
                <TextField
                  fullWidth
                  label="Volume"
                  margin="normal"
                  variant="outlined"
                  type="number"
                  value={buildingRepairDataEdit.volume}
                  sx={{ mb: 1 }}
                  name="volume"
                  disabled={!isEditing}
                  error={isEditing && errorsEdit.volume}
                  helperText={
                    isEditing && errorsEdit.volume ? "Mandatory Field" : ""
                  }
                  onChange={handleNumberInputChangeEdit}
                />
                <TextField
                  fullWidth
                  label="Duration (days)"
                  margin="normal"
                  variant="outlined"
                  type="number"
                  value={buildingRepairDataEdit.duration || ""}
                  sx={{ mb: 1 }}
                  name="duration"
                  disabled={!isEditing}
                  error={isEditing && errorsEdit.duration}
                  helperText={
                    isEditing && errorsEdit.duration ? "Mandatory Field" : ""
                  }
                  onChange={handleNumberInputChangeEdit}
                  inputProps={{ min: 1 }}
                />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="Start Date"
                      format="YYYY-MM-DD"
                      value={
                        buildingRepairDataEdit.startDate
                          ? dayjs(buildingRepairDataEdit.startDate)
                          : null
                      }
                      onChange={(newValue) =>
                        handleDateChangeEdit(
                          "startDate",
                          newValue?.toDate() || null
                        )
                      }
                      disabled={!isEditing}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { mb: 1 },
                          error: isEditing && errorsEdit.startDate,
                          helperText:
                            isEditing && errorsEdit.startDate
                              ? "Mandatory Field"
                              : "",
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="End Date"
                      format="YYYY-MM-DD"
                      value={
                        buildingRepairDataEdit.endDate
                          ? dayjs(buildingRepairDataEdit.endDate)
                          : null
                      }
                      onChange={(newValue) =>
                        handleDateChangeEdit(
                          "endDate",
                          newValue?.toDate() || null
                        )
                      }
                      disabled
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { mb: 1 },
                          error: isEditing && errorsEdit.endDate,
                          helperText:
                            isEditing && errorsEdit.endDate
                              ? "Mandatory Field"
                              : "",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Payment Status"
                    margin="normal"
                    variant="outlined"
                    value={buildingRepairDataEdit.paymentStatus}
                    sx={{ mb: 1 }}
                    name="paymentStatus"
                    disabled={!isEditing}
                    error={isEditing && errorsEdit.paymentStatus}
                    helperText={
                      isEditing && errorsEdit.paymentStatus
                        ? "Mandatory Field"
                        : ""
                    }
                    onChange={handleInputChangeEdit}
                  >
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Unpaid">Unpaid</MenuItem>
                  </TextField>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="Payment Date"
                      format="YYYY-MM-DD"
                      value={
                        buildingRepairDataEdit.paymentDate
                          ? dayjs(buildingRepairDataEdit.paymentDate)
                          : null
                      }
                      onChange={(newValue) =>
                        handleDateChangeEdit(
                          "paymentDate",
                          newValue?.toDate() || null
                        )
                      }
                      disabled={!isEditing}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { mb: 1 },
                          margin: "normal",
                          // error: isEditing && errorsEdit.paymentDate,
                          // helperText:
                          //   isEditing && errorsEdit.paymentDate
                          //     ? "Mandatory Field"
                          //     : "",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                {/* Payment Evidence Upload */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: editPaymentEvidencePreview ? "2px solid #aaa" : "2px dashed #aaa",
                    padding: 4,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                    textAlign: "center",
                    cursor: isEditing ? "pointer" : "default",
                    position: "relative",
                    opacity: isEditing ? 1 : 0.7,
                  }}
                  onClick={() => isEditing && editPaymentEvidenceInputRef.current?.click()}
                >
                  {editPaymentEvidencePreview ? (
                    editPaymentEvidencePreview === "pdf_uploaded" ? (
                      <>
                        <GridOn fontSize="large" color={isEditing ? "primary" : "disabled"} />
                        <Typography variant="body2" mt={1}>
                          {generateDisplayPaymentFilename(buildingRepairDataEdit.rabName)}
                        </Typography>
                        {buildingRepairDataEdit.paymentEvidence && (
                          <Typography variant="caption" color="textSecondary">
                            Original ID: {buildingRepairDataEdit.paymentEvidence}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <img
                          src={editPaymentEvidencePreview}
                          alt="Payment Evidence Preview"
                          style={{
                            width: "100%",
                            height: "auto",
                            borderRadius: "8px",
                            objectFit: "contain",
                            maxHeight: 180,
                          }}
                        />
                        {buildingRepairDataEdit.paymentEvidence && (
                          <Typography variant="caption" color="textSecondary" mt={1}>
                            Original: {buildingRepairDataEdit.paymentEvidence}
                          </Typography>
                        )}
                      </>
                    )
                  ) : (
                    <>
                      <GridOn fontSize="large" color={isEditing ? "action" : "disabled"} />
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        {isEditing
                          ? "Drag & drop or click to upload payment evidence"
                          : "No payment evidence uploaded"}
                      </Typography>
                    </>
                  )}

                  {/* Download and Delete Buttons */}
                  {editPaymentEvidencePreview && isEditing && (
                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (buildingRepairDataEdit.paymentEvidence) {
                            handleDownloadPayment(buildingRepairDataEdit.paymentEvidence);
                          }
                        }}
                      >
                        DOWNLOAD
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFileEdit("paymentEvidence");
                        }}
                      >
                        DELETE
                      </Button>
                    </Box>
                  )}

                  <input
                    hidden
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    type="file"
                    ref={editPaymentEvidenceInputRef}
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleEditFileUpload(e.target.files[0], "paymentEvidence");
                      }
                    }}
                    disabled={!isEditing}
                  />
                  <Typography variant="caption" mt={1}>
                    Max size: 5MB (PDF, CSV/XLS/XLSX, WORD)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Approval RAB</Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    select
                    fullWidth
                    disabled={!isEditing || !isApprover}
                    label="Status Approval"
                    placeholder="Status Approval"
                    value={Object.keys(statusesValue).find(key => statusesValue[key] === buildingRepairDataEdit.isApprove)}
                    onChange={(e) => setBuildingRepairDataEdit({ ...buildingRepairDataEdit, isApprove: statusesValue[e.target.value] })}
                  // error={!editRabData.status}
                  // helperText={!editRabData.status ? "Mandatory Field" : ""}
                  >
                    {statuses.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Autocomplete
                    fullWidth
                    disabled={!isEditing || !isApprover}
                    options={employeeOptions}
                    getOptionLabel={(option) => option.name || ""}
                    value={employeeOptions.find(opt => opt.nik === buildingRepairDataEdit.approverName) || null}
                    onChange={(event, newValue) => {
                      console.log(newValue);
                      setBuildingRepairDataEdit((prev) => ({
                        ...prev,
                        approverName: newValue?.nik || "",
                        approverPosition: newValue?.jobTitle || "",
                      }));
                      console.log(buildingRepairDataEdit)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employee Name" name="name"
                        placeholder="Employee name"
                        fullWidth
                      />
                    )}
                  />
                  <TextField
                    fullWidth
                    disabled
                    label="Posisi"
                    placeholder="Posisi"
                    value={buildingRepairDataEdit.approverPosition || ''}
                  />
                </Box>

              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        {/* Buttons: Save & Close */}
        <DialogActions sx={{ justifyContent: "space-between", padding: 2 }}>
          {!isEditing ? (
            <Button
              onClick={() => setIsEditing(true)}
              variant="contained"
              color="primary"
              sx={{ width: 120 }}
            >
              EDIT
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setIsEditing(false)}
                variant="outlined"
                color="error"
                sx={{ width: 120 }}
              >
                CANCEL
              </Button>
              <Button
                onClick={handleSaveEdit}
                variant="contained"
                color="success"
                sx={{ width: 120 }}
              >
                SAVE
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BuildingRepairListPage;
